import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useBooking } from "../context/BookingContext";
import { usePricing, isNightSlot, SPORT_COURT_MAP } from "../context/PricingContext";
import { usePromo } from "../context/PromoContext";
import { getPartnerDiscount } from "../utils/partnerCodes";

const RAZORPAY_KEY = "rzp_live_SgDOmK8a3Gwfms";

// ── localStorage recovery — survives page close if Firebase write failed ───────
const RECOVERY_KEY = "tsa_payment_recovery";

function saveRecovery(record) {
  try {
    const existing = JSON.parse(localStorage.getItem(RECOVERY_KEY) || "[]");
    existing.push(record);
    localStorage.setItem(RECOVERY_KEY, JSON.stringify(existing));
    console.log("[Payment] Saved recovery record to localStorage:", record);
  } catch (e) {
    console.error("[Payment] Failed to save recovery record:", e);
  }
}

function clearRecovery(bookingId) {
  try {
    const existing = JSON.parse(localStorage.getItem(RECOVERY_KEY) || "[]");
    const updated = existing.filter((r) => r.bookingId !== bookingId);
    localStorage.setItem(RECOVERY_KEY, JSON.stringify(updated));
    console.log("[Payment] Cleared recovery record for:", bookingId);
  } catch (e) {
    console.error("[Payment] Failed to clear recovery record:", e);
  }
}

// ── sessionStorage: preserve booking state across Android UPI app redirects ───
// When Chrome on Android opens a UPI app (GPay/PhonePe), the browser tab can
// reload on return, wiping React state. We save state before rzp.open() and
// restore it on mount so the user lands back on PaymentSummary with state intact.
const SESSION_KEY = "tsa_payment_state";

function savePaymentSession(data) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ...data, savedAt: Date.now() }));
    console.log("[Session] Saved payment state to sessionStorage");
  } catch (e) {
    console.warn("[Session] Save failed:", e.message);
  }
}

function loadPaymentSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Expire after 30 minutes — same window as booking expiry
    if (Date.now() - (data.savedAt || 0) > 30 * 60 * 1000) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return data;
  } catch { return null; }
}

function clearPaymentSession() {
  try { sessionStorage.removeItem(SESSION_KEY); } catch (_) {}
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Retries updateFn up to maxRetries times with 1-second delay between attempts.
// Returns true if any attempt succeeds, false if all fail.
async function updateWithRetry(updateFn, id, status, extra, maxRetries = 3) {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`[Payment] Firestore update attempt ${attempt}/${maxRetries} — ${id} → ${status}`);
      await updateFn(id, status, extra);
      console.log(`[Payment] Firestore update succeeded on attempt ${attempt}`);
      return true;
    } catch (e) {
      console.error(`[Payment] Attempt ${attempt}/${maxRetries} failed:`, e.message);
      if (attempt < maxRetries) {
        console.log("[Payment] Retrying in 1 second...");
        await sleep(1000);
      }
    }
  }
  console.error(`[Payment] All ${maxRetries} attempts failed for ${id}`);
  return false;
}

function formatTime12(t) {
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${period}`;
}

function slotEndTime(t) {
  const [h, m] = t.split(":").map(Number);
  const endH = (h + 1) % 24;
  return `${String(endH).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function slotHour(t) {
  const h = parseInt(t, 10);
  return h < 5 ? h + 24 : h;
}

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function PaymentSummary() {
  const navigate = useNavigate();
  const {
    selectedDate, setSelectedDate,
    selectedSlots, setSelectedSlots,
    bookingForm, setBookingForm,
    calcTotal,
    createPendingBooking, updateBookingStatus, finalizeBooking,
    selectedSport, setSelectedSport,
    partnerCode, setPartnerCode,
  } = useBooking();
  const { getSportRate, getSlotPrice }  = usePricing();
  const { calcDiscount }  = usePromo();

  const [paying, setPaying]               = useState(false);
  const [error, setError]                 = useState(null);
  const [confirmedBooking, setConfirmed]  = useState(null);
  const [sessionRestored, setSessionRestored] = useState(false);

  // Track the current pending booking ID so retries reuse it until it's marked failed/cancelled
  const pendingIdRef = useRef(null);
  // Set to true when we're restoring from sessionStorage — prevents guard from firing
  const restoringRef = useRef(false);

  // ── 1. Restore from sessionStorage (MUST be first effect) ──────────────────
  // Runs only on mount. If React state is empty (UPI redirect reloaded the page),
  // we restore from sessionStorage so the user lands back on PaymentSummary intact.
  useEffect(() => {
    if (selectedSlots.length > 0) return; // state is already present, nothing to restore
    const saved = loadPaymentSession();
    if (!saved?.selectedSlots?.length) return;

    console.log("[Session] Restoring payment state after UPI redirect:", saved);
    restoringRef.current = true;

    setSelectedSlots(saved.selectedSlots);
    if (saved.bookingForm)  setBookingForm(saved.bookingForm);
    if (saved.selectedSport) setSelectedSport(saved.selectedSport);
    if (saved.selectedDate)  setSelectedDate(saved.selectedDate);
    if (saved.partnerCode !== undefined) setPartnerCode(saved.partnerCode);

    setSessionRestored(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-hide the "Resuming your payment..." toast after 3 seconds
  useEffect(() => {
    if (!sessionRestored) return;
    const t = setTimeout(() => setSessionRestored(false), 3000);
    return () => clearTimeout(t);
  }, [sessionRestored]);

  // Navigate after confirmedBooking state is set (avoids mid-render navigate)
  useEffect(() => {
    if (confirmedBooking) {
      navigate("/confirmation", { state: { booking: confirmedBooking } });
    }
  }, [confirmedBooking, navigate]);

  // Guard — skip if confirmed (slots/form cleared by finalizeBooking) or restoring from session
  useEffect(() => {
    if (confirmedBooking) return;
    if (restoringRef.current) return; // session restore in progress — don't redirect
    if (!selectedSlots.length) navigate("/book");
    else if (!bookingForm.name) navigate("/booking-form");
  }, [selectedSlots.length, bookingForm.name, confirmedBooking, navigate]);

  // ── Recovery: on mount, retry any Firebase writes that failed in a previous session ──
  useEffect(() => {
    let records;
    try { records = JSON.parse(localStorage.getItem(RECOVERY_KEY) || "[]"); }
    catch { records = []; }
    if (!records.length) return;

    console.log("[Recovery] Found", records.length, "stuck payment record(s):", records);
    records.forEach(async (record) => {
      console.log("[Recovery] Retrying Firebase update for:", record.bookingId, "payment:", record.razorpay_payment_id);
      try {
        await updateBookingStatus(record.bookingId, "confirmed", {
          razorpay_payment_id: record.razorpay_payment_id,
        });
        console.log("[Recovery] Successfully recovered booking:", record.bookingId);
        clearRecovery(record.bookingId);
      } catch (e) {
        console.error("[Recovery] Recovery failed for:", record.bookingId, e.message);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!selectedSlots.length || !bookingForm.name) return null;

  const courtKey        = SPORT_COURT_MAP[selectedSport?.name] || "Oval Cricket Ground";
  const dayRate         = getSportRate(selectedDate, selectedSport?.name); // day rate for promo reference
  const daySlots        = selectedSlots.filter((s) => !isNightSlot(s));
  const nightSlots      = selectedSlots.filter((s) =>  isNightSlot(s));
  const dayTotal        = daySlots.reduce((sum, s) => sum + getSlotPrice(courtKey, s, selectedDate), 0);
  const nightTotal      = nightSlots.reduce((sum, s) => sum + getSlotPrice(courtKey, s, selectedDate), 0);
  const rawTotal        = calcTotal();
  const promoDiscount   = calcDiscount(rawTotal, selectedSlots.length, dayRate);
  const afterPromo      = rawTotal - promoDiscount;
  const partnerDiscount = getPartnerDiscount(partnerCode, afterPromo);
  const finalTotal      = afterPromo - partnerDiscount;
  const sorted     = [...selectedSlots].sort((a, b) => slotHour(a) - slotHour(b));
  const hasNextDay = sorted.some((s) => parseInt(s, 10) < 5);
  const dateLabel  = new Date(selectedDate + "T00:00:00").toLocaleDateString("en-IN", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
  const nextDayLabel = (() => {
    if (!hasNextDay || !selectedDate) return "";
    const d = new Date(selectedDate + "T00:00:00");
    d.setDate(d.getDate() + 1);
    return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "long" });
  })();

  const handlePay = async () => {
    setError(null);

    // Guard: pricing must be loaded and non-zero before allowing payment
    if (!finalTotal || !isFinite(finalTotal) || finalTotal <= 0) {
      setError("Pricing could not be calculated. Please go back and re-select your slots.");
      return;
    }

    setPaying(true);

    // Load Razorpay SDK
    const loaded = await loadRazorpayScript();
    if (!loaded) {
      setError("Could not load the payment gateway. Please check your connection and try again.");
      setPaying(false);
      return;
    }

    // Create a new pending booking ID (fresh each attempt after a failure)
    const id = "BK" + Date.now().toString().slice(-6);
    pendingIdRef.current = id;

    const partnerExtra = partnerCode
      ? { partnerCode, partnerDiscount }
      : {};

    let bk;
    try {
      bk = await createPendingBooking(id, finalTotal, { ...partnerExtra, source: "online" });
    } catch (e) {
      if (e.message === "SLOT_TAKEN") {
        setError("This slot was just booked by someone else. Please go back and pick another time.");
      } else {
        setError("Could not save booking. Please try again.");
        // Fire-and-forget: log the real error so we can debug silent failures
        addDoc(collection(db, "booking_errors"), {
          timestamp:    serverTimestamp(),
          errorMessage: e.message  || null,
          errorCode:    e.code     || null,
          errorStack:   e.stack    ? e.stack.slice(0, 1000) : null,
          phone:        bookingForm.phone || null,
          name:         bookingForm.name  || null,
          sport:        selectedSport?.name || null,
          date:         selectedDate || null,
          slots:        selectedSlots,
          totalAttempted: finalTotal,
          userAgent:    typeof navigator !== "undefined" ? navigator.userAgent : null,
          platform:     typeof navigator !== "undefined" ? navigator.platform  : null,
          viewport:     typeof window    !== "undefined" ? `${window.innerWidth}x${window.innerHeight}` : null,
          referrer:     typeof document  !== "undefined" ? (document.referrer || null) : null,
        }).catch(() => {}); // never block the user if logging itself fails
      }
      setPaying(false);
      return;
    }

    // ── Race-condition guard ─────────────────────────────────────────────────
    // Set to true the moment Razorpay calls handler() — before any async work.
    // ondismiss fires after modal closes (including after successful payment on mobile),
    // so it must check this flag before writing "failed" to Firebase.
    let paymentCaptured = false;

    const options = {
      key:         RAZORPAY_KEY,
      amount:      finalTotal * 100, // paise
      currency:    "INR",
      name:        "Town Square Arena",
      description: `${selectedSport?.name || "Oval Cricket"} · ${sorted.length} hr${sorted.length > 1 ? "s" : ""} · ${selectedDate}`,
      prefill: {
        name:    bookingForm.name,
        contact: bookingForm.phone,
      },
      notes: {
        bookingId: id,
      },
      theme: { color: "#f59e0b" },

      // ── Payment success ──────────────────────────────────────────────────────
      handler: async (response) => {
        const paymentId = response.razorpay_payment_id;
        console.log("[Payment] Handler called with payment_id:", paymentId);

        // Set flag IMMEDIATELY — ondismiss must not touch this booking now
        paymentCaptured = true;

        // Payment is confirmed — clear session (no longer needed)
        clearPaymentSession();

        // Try to confirm in Firebase with up to 3 attempts
        const succeeded = await updateWithRetry(
          updateBookingStatus, id, "confirmed",
          { razorpay_payment_id: paymentId }
        );

        if (!succeeded) {
          // Firebase failed after all retries — save locally so we can recover on next load
          saveRecovery({ bookingId: id, razorpay_payment_id: paymentId, savedAt: new Date().toISOString() });
          console.warn("[Payment] Showing success anyway — payment IS captured by Razorpay");
        } else {
          clearRecovery(id);
        }

        // Always show success page — money is captured regardless of our Firebase state
        finalizeBooking();
        setConfirmed({
          ...bk,
          status: "confirmed",
          razorpay_payment_id: paymentId,
        });
      },

      modal: {
        // ── Modal closed ────────────────────────────────────────────────────
        // Fires when user dismisses WITHOUT paying, but ALSO fires after a
        // successful payment on mobile once the modal auto-closes. The flag
        // paymentCaptured distinguishes these two cases.
        // NOTE: ondismiss only fires if the React app is still alive — if the
        // page reloaded (UPI app redirect), this callback never runs. In that
        // case, the session restore handles recovery instead.
        ondismiss: async () => {
          console.log("[Payment] Modal dismissed, paymentCaptured =", paymentCaptured);

          if (paymentCaptured) {
            // Payment already went through — ondismiss is just the modal closing.
            // Do NOT mark the booking as failed.
            console.log("[Payment] ondismiss ignored — payment was already captured");
            return;
          }

          // Genuine dismissal (user closed without paying)
          // Safe to clear session here — if page had reloaded, this callback wouldn't fire.
          clearPaymentSession();
          console.log("[Payment] ondismiss marking booking as failed");
          try { await updateBookingStatus(id, "failed", { failureReason: "user_dismissed" }); } catch (_) {}
          pendingIdRef.current = null;
          setError("Payment was cancelled. You can try again.");
          setPaying(false);
        },
      },
    };

    let rzp;
    try {
      console.log("[Payment] Creating Razorpay instance with amount:", finalTotal * 100, "paise");
      rzp = new window.Razorpay(options);
      console.log("[Payment] Razorpay instance created successfully");
    } catch (e) {
      console.error("[Payment] Failed to create Razorpay instance:", e);
      clearPaymentSession();
      try { await updateBookingStatus(id, "failed", { failureReason: "razorpay_error", errorMessage: e.message, error_description: e.message }); } catch (_) {}
      pendingIdRef.current = null;
      setError("Could not initialise the payment gateway. Please try again.");
      setPaying(false);
      return;
    }

    // ── Payment failed (card declined, etc.) ────────────────────────────────
    rzp.on("payment.failed", async (response) => {
      console.log("[Payment] payment.failed event:", response.error.code, response.error.description);
      clearPaymentSession();
      try {
        await updateBookingStatus(id, "failed", {
          failureReason:     "payment_failed",
          errorMessage:      response.error.description || null,
          error_code:        response.error.code,
          error_description: response.error.description,
        });
      } catch (_) {}
      pendingIdRef.current = null;
      setError(`Payment failed: ${response.error.description}. Please try again.`);
      setPaying(false);
    });

    // ── Save state to sessionStorage before opening Razorpay ────────────────
    // If Chrome on Android launches a UPI app and the tab reloads on return,
    // React state is lost. This save ensures we can restore on remount.
    savePaymentSession({
      selectedSlots,
      bookingForm,
      selectedSport,
      selectedDate,
      partnerCode: partnerCode || null,
      finalTotal,
    });

    try {
      console.log("[Payment] Opening Razorpay modal for booking:", id);
      rzp.open();
      console.log("[Payment] rzp.open() returned — modal should be visible");
    } catch (e) {
      console.error("[Payment] rzp.open() threw:", e);
      clearPaymentSession();
      try { await updateBookingStatus(id, "failed", { failureReason: "browser_error", errorMessage: e.message, error_description: e.message }); } catch (_) {}
      pendingIdRef.current = null;
      setError("Could not open the payment window. Please try again.");
      setPaying(false);
    }
  };

  return (
    <div className="bg-white" style={{ paddingTop: 64 }}>
      <div className="max-w-lg mx-auto px-4 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-black/30 text-xs mb-8">
            <button onClick={() => navigate("/book")} className="hover:text-black/60 transition-colors">Select Slots</button>
            <span>→</span>
            <button onClick={() => navigate("/booking-form")} className="hover:text-black/60 transition-colors">Your Details</button>
            <span>→</span>
            <span className="text-[#f59e0b] font-semibold">Payment</span>
          </div>

          {/* Title */}
          <h1
            className="text-black font-black text-3xl sm:text-4xl tracking-tight mb-1"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Payment Summary
          </h1>
          <p className="text-black/40 text-sm mb-7">Review your booking before paying</p>

          {/* Session restore toast */}
          {sessionRestored && (
            <div className="flex items-center gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
              <span className="text-amber-500 text-base flex-shrink-0">↩</span>
              <p className="text-amber-800 text-sm font-medium">Resuming your payment...</p>
            </div>
          )}

          {/* Summary card */}
          <div className="rounded-2xl border border-black/8 overflow-hidden mb-5" style={{ background: "#f9fafb" }}>

            {/* Booking info */}
            <div className="px-5 pt-5 pb-4">
              <p className="text-[10px] font-black text-black/40 uppercase tracking-[0.2em] mb-2">Booking Info</p>
              <p className="text-black font-bold text-base leading-tight">{dateLabel}</p>
              <p className="text-black/50 text-sm mt-0.5">
                {formatTime12(sorted[0])} → {formatTime12(slotEndTime(sorted[sorted.length - 1]))}
                {hasNextDay && <span className="text-black/30 text-xs ml-1">({nextDayLabel})</span>}
              </p>
            </div>

            {/* Booking details rows */}
            <div className="px-5 pb-4 space-y-2 text-sm">
              {[
                ["Name",  bookingForm.name],
                ["Phone", bookingForm.phone],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between">
                  <span className="text-black/40">{label}</span>
                  <span className="text-black font-medium">{value}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-black/8 mx-5" />

            {/* Price breakdown */}
            <div className="px-5 py-4 space-y-2 text-sm">
              {daySlots.length > 0 && (
                <div className="flex justify-between text-black/50">
                  <span>☀️ {daySlots.length} day slot{daySlots.length > 1 ? "s" : ""}</span>
                  <span className="text-black">₹{dayTotal.toLocaleString()}</span>
                </div>
              )}
              {nightSlots.length > 0 && (
                <div className="flex justify-between text-black/50">
                  <span>🌙 {nightSlots.length} night slot{nightSlots.length > 1 ? "s" : ""}</span>
                  <span className="text-black">₹{nightTotal.toLocaleString()}</span>
                </div>
              )}
              {daySlots.length > 0 && nightSlots.length > 0 && (
                <div className="flex justify-between text-black/40 text-xs border-t border-black/6 pt-2">
                  <span>Subtotal</span>
                  <span>₹{rawTotal.toLocaleString()}</span>
                </div>
              )}
              {promoDiscount > 0 && (
                <div className="flex justify-between text-green-600 font-medium">
                  <span>🎁 Promo Applied ✓</span>
                  <span>–₹{promoDiscount.toLocaleString()}</span>
                </div>
              )}
              {partnerDiscount > 0 && (
                <div className="flex justify-between text-purple-600 font-medium">
                  <span>🏷 {partnerCode} (15% off)</span>
                  <span>–₹{partnerDiscount.toLocaleString()}</span>
                </div>
              )}
            </div>

            <div className="border-t border-black/8 mx-5" />

            {/* Total */}
            <div className="px-5 py-4 flex justify-between items-center">
              <span className="text-black font-black text-base">Total Payable</span>
              <span className="text-[#f59e0b] font-black text-2xl leading-none tracking-tight">
                ₹{finalTotal.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Error banner — shown after failed/cancelled payment */}
          {error && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">
              <span className="text-red-500 text-base flex-shrink-0 mt-0.5">⚠️</span>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Pay button */}
          <button
            onClick={handlePay}
            disabled={paying}
            className="w-full bg-[#f59e0b] hover:bg-amber-400 text-black font-black text-lg py-4 rounded-2xl transition-all hover:shadow-xl hover:shadow-[#f59e0b]/25 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60"
          >
            {paying ? "Opening payment..." : `Pay ₹${finalTotal.toLocaleString()} →`}
          </button>

          <p className="text-black/25 text-xs text-center mt-4">
            By proceeding you agree to our cancellation and refund policy
          </p>

        </motion.div>
      </div>
    </div>
  );
}
